/**
 * @file LanguagePicker.tsx
 * @brief S2S language selection — pill trigger + searchable modal sheet.
 *        Multi-select shows a mobile-style overlapping flag stack after pick.
 *
 * @author DES
 * @date 19-06-2026
 * @updated 01-10-2026: `variant="header"` (single-select): the page-header
 *          `HeaderFilterMenu` pill; its first row is the placeholder ("any")
 */

import { useMemo, useState, type ReactElement } from "react";
import { ChevronDown, ChevronsUpDown, Earth, Languages, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { HeaderFilterMenu } from "@/components/ui/HeaderFilterMenu";
import { LanguageFlag } from "@/components/ui/LanguageFlag";
import { PickerSheet, type PickerSheetItem } from "@/components/ui/PickerSheet";
import { apiGet } from "@/shared/api/client";
import { scheduleEndpoints } from "@/shared/api/endpoints";
import { cn } from "@/shared/theme/cn";
import { languageBaseCode } from "@/shared/utils/languages";
import { z } from "zod";

const S2SLanguagesResponseSchema = z.object({
  success: z.boolean().optional(),
  languages: z.array(
    z.object({
      code: z.string(),
      label: z.string(),
      locale: z.string().optional(),
    }),
  ),
});

export type S2SLanguage = {
  code: string;
  label: string;
  locale?: string;
};

// Not exported on purpose: nothing outside this file uses it, and a non-component
// export alongside `LanguagePicker` costs the module its Fast Refresh — every edit
// full-reloads the page instead of hot-swapping. (02-08-2026)
function languageId(lang: S2SLanguage): string {
  return lang.locale || lang.code;
}

function findLanguage(languages: S2SLanguage[], value?: string | null): S2SLanguage | undefined {
  if (!value) return undefined;
  return languages.find((l) => l.code === value || l.locale === value || languageId(l) === value);
}

interface LanguagePickerItem extends PickerSheetItem {
  language: S2SLanguage;
}

type LanguagePickerBase = {
  placeholder?: string;
  disabled?: boolean;
  size?: "sm" | "md";
  /**
   * `field` (30-09-2026): the mobile MeetingSetup picker row, bare inside a
   * Fieldset: icon, 17px value, chevron, no box, no "Tap to manage".
   * `header` (01-10-2026, single-select only): the 56px page-header filter
   * pill beside the date circle.
   */
  variant?: "pill" | "select" | "field" | "header";
  languages?: S2SLanguage[];
  loading?: boolean;
  /** Extra classes for the trigger button (e.g. flush filter cells). */
  triggerClassName?: string;
  /**
   * (01-10-2026, interpretation screen) Hide every language sharing this base
   * code, e.g. the other speaker's language, so both sides can't pick the same.
   */
  excludeBaseCode?: string | null;
  /** Override the menu's title / subtitle ("Language for" / "Person B"). */
  title?: string;
  subtitle?: string;
  /**
   * (01-10-2026) Single-select only: render your own trigger (the interpreter's
   * language pills). Must be a native element or forward its ref: it becomes
   * the menu anchor.
   */
  renderTrigger?: (matched: S2SLanguage | undefined) => ReactElement;
  /**
   * (04-10-2026, DES) Single-select, pill/select trigger: once a language is
   * picked (or pre-filled from last time) an ✕ replaces the chevron and
   * clears it in one click, without opening the menu.
   */
  clearable?: boolean;
  /**
   * (04-10-2026, DES) Recently used languages, newest first (codes or
   * locales). Up to 6 are lifted to the top of the menu under "Recent", the
   * rest follow under "All languages", as the phone's VZLanguagePicker.
   */
  recentIds?: string[];
};

type LanguagePickerSingle = LanguagePickerBase & {
  multiSelect?: false;
  value?: string | null;
  onChange: (languageId: string | null) => void;
  values?: never;
  onValuesChange?: never;
};

type LanguagePickerMulti = LanguagePickerBase & {
  multiSelect: true;
  values: string[];
  onValuesChange: (languageIds: string[]) => void;
  value?: never;
  onChange?: never;
};

export type LanguagePickerProps = LanguagePickerSingle | LanguagePickerMulti;

const STACK_OVERLAP = 18;
/** Flags shown in the stack before a "+N" chip; 12 ran off the card. */
const STACK_MAX = 5;

export function LanguagePicker(props: LanguagePickerProps) {
  const {
    placeholder = "Select language…",
    disabled,
    size = "md",
    variant = "pill",
    languages: languagesProp,
    loading: loadingProp,
    triggerClassName,
    excludeBaseCode,
    title: titleProp,
    subtitle: subtitleProp,
    renderTrigger,
    clearable,
    recentIds,
  } = props;
  const multiSelect = props.multiSelect === true;
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetched = useQuery({
    queryKey: ["s2s", "languages"],
    queryFn: async () => {
      const raw = await apiGet<unknown>(scheduleEndpoints.languages);
      const parsed = S2SLanguagesResponseSchema.parse(raw);
      return parsed.languages;
    },
    enabled: languagesProp === undefined,
    staleTime: 5 * 60_000,
  });

  const languages = useMemo(
    () => languagesProp ?? fetched.data ?? [],
    [languagesProp, fetched.data],
  );
  const loading = loadingProp ?? (languagesProp === undefined && fetched.isLoading);

  const multiValues = multiSelect ? props.values : undefined;
  const values = useMemo(() => multiValues ?? [], [multiValues]);
  const singleValue = multiSelect ? null : (props.value ?? null);

  const matched = useMemo(() => findLanguage(languages, singleValue), [languages, singleValue]);

  const matchedMulti = useMemo(() => {
    if (!multiSelect) return [];
    return values
      .map((id) => findLanguage(languages, id))
      .filter((l): l is S2SLanguage => Boolean(l));
  }, [languages, multiSelect, values]);

  const items: LanguagePickerItem[] = useMemo(
    () =>
      languages
        .filter(
          (language) =>
            !excludeBaseCode ||
            languageBaseCode(languageId(language)) !== languageBaseCode(excludeBaseCode),
        )
        .map((language) => ({
        id: languageId(language),
        label: language.label,
        subtitle: language.locale ?? language.code,
        searchKeys: [language.code, language.locale ?? ""],
        language,
      })),
    [languages, excludeBaseCode],
  );

  // Recent first (max 6, as the phone), then everything else.
  const orderedItems: LanguagePickerItem[] = useMemo(() => {
    if (!recentIds?.length) return items;
    const byId = new Map(items.map((it) => [it.id, it]));
    const recent: LanguagePickerItem[] = [];
    for (const raw of recentIds) {
      const lang = findLanguage(languages, raw);
      const it = lang ? byId.get(languageId(lang)) : undefined;
      if (it && !recent.includes(it)) recent.push(it);
      if (recent.length === 6) break;
    }
    if (recent.length === 0) return items;
    const lifted = new Set(recent);
    return [
      ...recent.map((it) => ({ ...it, group: "Recent" })),
      ...items.filter((it) => !lifted.has(it)).map((it) => ({ ...it, group: "All languages" })),
    ];
  }, [items, recentIds, languages]);

  const triggerHeight = variant === "select" ? "h-11" : size === "sm" ? "h-9" : "h-10";

  // No catalog count in the title (DES 06-10-2026, as on the phone).
  const sheetTitle = multiSelect ? "Select languages" : "Select language";

  const summaryLabel = (() => {
    if (loading) return "Loading…";
    if (multiSelect) {
      const n = matchedMulti.length;
      if (n === 0) return placeholder;
      return `${n} language${n === 1 ? "" : "s"}`;
    }
    return matched?.label ?? placeholder;
  })();

  const handleSelect = (item: LanguagePickerItem) => {
    if (multiSelect) {
      const next = values.includes(item.id)
        ? values.filter((id) => id !== item.id)
        : [...values, item.id];
      if (next.length === 0) return;
      props.onValuesChange(next);
      return;
    }
    props.onChange(item.id);
  };

  const handleClear = () => {
    if (multiSelect) {
      const en = findLanguage(languages, "en-GB") || findLanguage(languages, "en") || languages[0];
      props.onValuesChange(en ? [languageId(en)] : []);
      setExpanded(false);
      return;
    }
    props.onChange(null);
  };

  const removeLanguage = (id: string) => {
    if (!multiSelect) return;
    if (values.length <= 1) return;
    props.onValuesChange(values.filter((v) => v !== id));
  };

  if (variant === "header" && !multiSelect) {
    const ANY = "__any__";
    return (
      <HeaderFilterMenu
        label={summaryLabel}
        active={Boolean(matched)}
        onClear={handleClear}
        icon={
          matched ? (
            <LanguageFlag language={matched} size="md" className="h-9 w-9" />
          ) : (
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand/10 text-brand">
              <Languages className="h-4 w-4" />
            </span>
          )
        }
        title="Filter by language"
        searchPlaceholder="Search languages"
        items={[
          {
            value: ANY,
            label: placeholder,
            icon: (
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand/10 text-brand">
                <Earth className="h-3.5 w-3.5" aria-hidden />
              </span>
            ),
          },
          ...items.map((item) => ({
            value: item.id,
            label: item.label,
            description: item.subtitle,
            keywords: item.searchKeys?.join(" "),
            icon: <LanguageFlag language={item.language} size="sm" />,
          })),
        ]}
        loading={loading}
        disabled={disabled}
        selected={matched ? languageId(matched) : ANY}
        onSelect={(id) => (id === ANY ? props.onChange?.(null) : props.onChange?.(id))}
      />
    );
  }

  return (
    <div className="space-y-3">
      {/* Mobile-style overlapping flag stack → expand to removable pills */}
      {multiSelect && matchedMulti.length > 0 ? (
        <div className="min-h-14">
          {!expanded ? (
            <button
              type="button"
              disabled={disabled}
              onClick={() => setExpanded(true)}
              className="flex items-center gap-3 py-1 text-left"
              aria-label="Show selected languages"
            >
              <span className="flex items-center">
                {matchedMulti.slice(0, STACK_MAX).map((lang, index) => (
                  <span
                    key={languageId(lang)}
                    className="relative inline-flex rounded-full ring-2 ring-background"
                    style={{
                      marginLeft: index === 0 ? 0 : -STACK_OVERLAP,
                      zIndex: matchedMulti.length - index,
                    }}
                  >
                    <LanguageFlag language={lang} size="lg" />
                  </span>
                ))}
                {matchedMulti.length > STACK_MAX ? (
                  <span
                    className="relative inline-flex h-14 w-14 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-foreground ring-2 ring-background"
                    style={{
                      marginLeft: -STACK_OVERLAP,
                      zIndex: matchedMulti.length + 1,
                    }}
                    aria-label={`${matchedMulti.length - STACK_MAX} more`}
                  >
                    +{matchedMulti.length - STACK_MAX}
                  </span>
                ) : null}
              </span>
              {variant !== "field" ? (
                <span className="text-xs text-muted-foreground">Tap to manage</span>
              ) : null}
            </button>
          ) : (
            <div className="flex flex-wrap gap-2">
              {matchedMulti.map((lang) => {
                const id = languageId(lang);
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-2 rounded-full bg-secondary/70 py-1 pl-1 pr-2"
                  >
                    <LanguageFlag language={lang} size="sm" />
                    <span className="max-w-[9rem] truncate text-sm font-medium">
                      {lang.label.split(" (")[0] || lang.label}
                    </span>
                    {values.length > 1 ? (
                      <button
                        type="button"
                        className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                        aria-label={`Remove ${lang.label}`}
                        onClick={() => removeLanguage(id)}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </span>
                );
              })}
              <button
                type="button"
                className="text-xs font-medium text-muted-foreground underline-offset-2 hover:underline"
                onClick={() => setExpanded(false)}
              >
                Collapse
              </button>
            </div>
          )}
        </div>
      ) : null}

      <PickerSheet<LanguagePickerItem>
        anchor={
          renderTrigger && !multiSelect ? (
            renderTrigger(matched)
          ) : variant === "field" ? (
            <button
              type="button"
              disabled={disabled || loading}
              className={cn(
                "flex min-h-11 w-full items-center gap-2 py-1 text-left",
                "rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                "disabled:cursor-not-allowed disabled:opacity-50",
                triggerClassName,
              )}
            >
              <Languages className="h-[22px] w-[22px] shrink-0 text-muted-foreground" aria-hidden />
              <span
                className={cn(
                  "min-w-0 flex-1 truncate text-[17px] font-medium",
                  (multiSelect ? matchedMulti.length : matched)
                    ? "text-foreground"
                    : "text-muted-foreground",
                )}
              >
                {multiSelect
                  ? matchedMulti.length > 0
                    ? summaryLabel
                    : placeholder || "Pick target languages"
                  : summaryLabel}
              </span>
              <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
            </button>
          ) : (
            <button
              type="button"
              disabled={disabled || loading}
              className={cn(
                "flex w-full items-center justify-between border bg-field transition-colors",
                variant === "select"
                  ? "rounded-[1.2rem] border-input/90 px-5 py-2 text-base font-medium text-foreground"
                  : "rounded-full border-input px-5 text-sm",
                triggerHeight,
                "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                "disabled:cursor-not-allowed disabled:opacity-50",
                !(multiSelect ? matchedMulti.length : matched) && "text-muted-foreground",
                triggerClassName,
              )}
            >
              <span className="flex min-w-0 items-center gap-2">
                {multiSelect ? (
                  <Earth className="h-4 w-4 shrink-0 opacity-60" />
                ) : matched ? (
                  <LanguageFlag language={matched} size="sm" />
                ) : (
                  <Earth className="h-4 w-4 shrink-0 opacity-60" />
                )}
                <span className="truncate">
                  {multiSelect
                    ? matchedMulti.length > 0
                      ? summaryLabel
                      : placeholder || "Pick target languages"
                    : summaryLabel}
                </span>
              </span>
              {clearable && !multiSelect && matched && !disabled ? (
                /* Inside the trigger, so it sits exactly where the chevron
                   was. Stops pointerdown (Radix opens the menu on it) and
                   click, so clearing never opens the picker. */
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Clear language"
                  title="Clear"
                  className="-m-1 rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleClear();
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter" && e.key !== " ") return;
                    e.stopPropagation();
                    e.preventDefault();
                    handleClear();
                  }}
                >
                  <X className="h-4 w-4" />
                </span>
              ) : (
                <ChevronsUpDown className="h-4 w-4 opacity-50" />
              )}
            </button>
          )
        }
        open={open}
        onOpenChange={(next) => {
          if (next) setExpanded(false);
          setOpen(next);
        }}
        title={titleProp ?? sheetTitle}
        subtitle={
          subtitleProp ??
          (multiSelect
            ? "Tap to select multiple target languages, then Done."
            : "Choose from enabled platform languages.")
        }
        searchPlaceholder="Search languages…"
        items={orderedItems}
        selectedId={matched ? languageId(matched) : null}
        selectedIds={multiSelect ? values : undefined}
        multiSelect={multiSelect}
        onSelect={handleSelect}
        onClear={handleClear}
        loading={loading}
        renderLeftAccessory={(item) => <LanguageFlag language={item.language} size="lg" />}
        emptyText="No languages available."
      />
    </div>
  );
}
